import { API_ENDPOINTS, httpClientInstance } from "@/api"
import { AccountMeResponse, AccountStatus } from "@/types/auth.types"
import { getTimeUntilExpiry, isTokenExpired, shouldRefreshToken } from "@/utils"
import axios from "axios"

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "http://localhost:8001/"

export interface LoginInitResponse {
  authorization_url: string
  state: string
  code_challenge: string
  expires_in: number
}

export interface AuthCallbackResponse {
  access_token: string
  refresh_token?: string
  token_type: string
  expires_in: number
  user: {
    id: string
    email: string
    name?: string
    picture?: string
  }
  /** Present only when login completed via a recovery code — Auth0 rotates it on use, show once. */
  recovery_code?: string
  /** Present only when remember_device was set during MFA verification. Persist and resend it. */
  device_token?: string
}

export interface MfaRequiredResponse {
  mfa_required: true
  mfa_token: string
  /** True when the user has no enrolled authenticator yet and must scan a new QR code. */
  enrollment_required?: boolean
  /** TOTP secret for manual entry, present when enrollment_required is true. */
  secret?: string
  /** otpauth:// URI to render as a QR code, present when enrollment_required is true. */
  barcode_uri?: string
  /**
   * One-time recovery codes, present only on first-ever MFA enrollment. Auth0
   * never returns these again — the user must save them now.
   */
  recovery_codes?: string[]
  /** Backend DEBUG-only detail explaining the enrollment check outcome. */
  debug_detail?: string
}

export interface LogoutResponse {
  message: string
  logout_url?: string
}

export interface RefreshTokenResponse {
  access_token: string
  refresh_token: string
  expires_in: number
  token_type: string
}

/**
 * Auth Service
 *
 * Handles authentication and authorization operations including:
 * - OAuth login flow with Auth0
 * - Token refresh and rotation
 * - User session management
 * - User profile fetching
 * - Automatic background token refresh
 *
 * All API calls use the centralized httpClient for consistent error handling.
 */
class AuthService {
  private readonly baseUrl: string
  private initializationPromise: Promise<boolean> | null = null
  private refreshTimer: ReturnType<typeof setTimeout> | null = null
  private isRefreshing: boolean = false

  constructor(baseUrl: string = API_BASE_URL) {
    this.baseUrl = baseUrl
  }

  /**
   * Initiate OAuth login flow
   * Returns authorization URL to redirect user to Auth0
   * @param forceLogin - If true, forces Auth0 to show login screen even if session exists
   */
  async initiateLogin(
    redirectUri: string = `${window.location.origin}/auth/callback`,
    forceLogin: boolean = true
  ): Promise<LoginInitResponse> {
    const response = await axios.post<LoginInitResponse>(
      `${this.baseUrl}${API_ENDPOINTS.auth.login}`,
      {
        redirect_uri: redirectUri,
        prompt: forceLogin ? "login" : undefined,
      },
      { timeout: 5000 } // Fast timeout for login initiation
    )

    // Store state and code_challenge in sessionStorage for validation during callback
    sessionStorage.setItem("oauth_state", response.data.state)
    sessionStorage.setItem("oauth_code_challenge", response.data.code_challenge)

    return response.data
  }

  /**
   * Handle OAuth callback from Auth0
   * Exchanges authorization code for access token
   */
  async handleCallback(code: string, state: string): Promise<AuthCallbackResponse> {
    try {
      // Validate state matches what we stored
      const storedState = sessionStorage.getItem("oauth_state")
      if (state !== storedState) {
        throw new Error("Invalid state parameter - possible CSRF attack")
      }

      const response = await axios.get<AuthCallbackResponse>(`${this.baseUrl}${API_ENDPOINTS.auth.callback}`, {
        params: { code, state },
      })

      // Store tokens in sessionStorage (session-scoped: cleared on browser close)
      if (response.data.access_token) {
        sessionStorage.setItem("access_token", response.data.access_token)
      }
      if (response.data.refresh_token) {
        sessionStorage.setItem("refresh_token", response.data.refresh_token)
      }
      if (response.data.user) {
        sessionStorage.setItem("user", JSON.stringify(response.data.user))
      }

      // Clean up session storage
      sessionStorage.removeItem("oauth_state")
      sessionStorage.removeItem("oauth_code_challenge")

      // Reset cached init result so useAuth re-evaluates with the new tokens
      this.initializationPromise = null

      return response.data
    } catch (error) {
      console.error("Failed to handle callback:", error)
      sessionStorage.removeItem("oauth_state")
      sessionStorage.removeItem("oauth_code_challenge")
      throw error
    }
  }

  /**
   * Authenticate with email + password via backend ROPG endpoint.
   * Stores tokens identically to handleCallback so the rest of the app
   * is unaware of which flow was used.
   *
   * If the account has MFA enabled, Auth0 cannot complete the password grant
   * directly — the backend instead returns { mfa_required, mfa_token }. The
   * caller must collect a one-time code and call verifyMfaCode(). Passing a
   * remembered deviceToken lets the backend complete MFA silently instead.
   */
  async loginWithCredentials(
    email: string,
    password: string,
    deviceToken?: string | null
  ): Promise<AuthCallbackResponse | MfaRequiredResponse> {
    const response = await axios.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.auth.credentialLogin}`,
      { email, password, device_token: deviceToken || undefined },
      { timeout: 15000 }
    )

    const data = response.data

    if (data.mfa_required) {
      return data as MfaRequiredResponse
    }

    return this._storeCredentialLoginTokens(data)
  }

  /**
   * Completes a credential login that returned mfa_required by verifying a
   * one-time code from the user's authenticator app. If rememberDevice is
   * true, the response may include a device_token to persist for future logins.
   */
  async verifyMfaCode(mfaToken: string, otp: string, rememberDevice = false): Promise<AuthCallbackResponse> {
    const response = await axios.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.auth.credentialLoginMfa}`,
      { mfa_token: mfaToken, otp, remember_device: rememberDevice },
      { timeout: 15000 }
    )

    return this._storeCredentialLoginTokens(response.data)
  }

  /**
   * Completes a credential login that returned mfa_required by verifying a
   * recovery code, for use when the authenticator app is unavailable.
   *
   * Auth0 invalidates the supplied code and rotates in a new one — the
   * returned AuthCallbackResponse.recovery_code (when present) must be shown
   * to the user, since it will not be shown again.
   */
  async verifyRecoveryCode(mfaToken: string, recoveryCode: string, rememberDevice = false): Promise<AuthCallbackResponse> {
    const response = await axios.post<any>(
      `${this.baseUrl}${API_ENDPOINTS.auth.credentialLoginRecovery}`,
      { mfa_token: mfaToken, recovery_code: recoveryCode, remember_device: rememberDevice },
      { timeout: 15000 }
    )

    return this._storeCredentialLoginTokens(response.data)
  }

  /**
   * Reads the remembered-device token used to skip MFA on this browser.
   * Stored in localStorage (not sessionStorage) because it must survive
   * browser close by design — that's the whole point of "remember this device".
   */
  getRememberedDeviceToken(): string | null {
    return localStorage.getItem("mfa_device_token")
  }

  storeRememberedDeviceToken(token: string): void {
    localStorage.setItem("mfa_device_token", token)
  }

  clearRememberedDeviceToken(): void {
    localStorage.removeItem("mfa_device_token")
  }

  /**
   * Stores tokens from a credential login (or MFA-completed login) response
   * and notifies listeners. Shared by loginWithCredentials, verifyMfaCode, and
   * verifyRecoveryCode.
   */
  private _storeCredentialLoginTokens(data: any): AuthCallbackResponse {
    if (data.access_token) {
      sessionStorage.setItem("access_token", data.access_token)
    }
    if (data.refresh_token) {
      sessionStorage.setItem("refresh_token", data.refresh_token)
    }

    // Members of a deactivated department sign in read-only; keep the server's
    // explanation for the banner (the read_only claim itself lives in the JWT).
    if (data.read_only && data.message) {
      sessionStorage.setItem("read_only_message", data.message)
    } else {
      sessionStorage.removeItem("read_only_message")
    }

    // Response shape differs by account_type: superadmin → user, member → member/sub_account
    const userObj = data.user || data.member || data.sub_account || null
    if (userObj) {
      sessionStorage.setItem("user", JSON.stringify(userObj))
    }

    // Reset cached init so useAuth re-evaluates with the new tokens
    this.initializationPromise = null

    // Notify listeners (useAuth event handler)
    this.dispatchAuthRefreshEvent()

    return data as AuthCallbackResponse
  }

  /**
   * Refresh access token using refresh token
   * Endpoint: POST /auth/refresh/
   * Payload: { "refresh_token": "string" }
   * Response: { "access_token": "string", "refresh_token": "string", "expires_in": number, "token_type": "Bearer" }
   *
   * Note: Backend implements token rotation - returns a new refresh_token on each refresh
   * Also fetches fresh user data after successful refresh
   * Prevents concurrent refresh attempts using a lock
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    // Prevent concurrent refresh attempts
    if (this.isRefreshing) {
      // Wait for the current refresh to complete
      await new Promise((resolve) => setTimeout(resolve, 1000))
      // Check if token was refreshed
      const newToken = this.getAccessToken()
      if (newToken && !this.isAccessTokenExpired(0)) {
        return {
          access_token: newToken,
          refresh_token: sessionStorage.getItem("refresh_token") || "",
          expires_in: this.getTokenExpiryTime(),
          token_type: "Bearer",
        }
      }
    }

    const refreshToken = sessionStorage.getItem("refresh_token")
    if (!refreshToken) {
      throw new Error("No refresh token available")
    }

    this.isRefreshing = true

    try {
      const response = await axios.post<RefreshTokenResponse>(
        `${this.baseUrl}${API_ENDPOINTS.auth.refresh}`,
        { refresh_token: refreshToken },
        { timeout: 10000 }
      )

      // Update both access_token and refresh_token (token rotation)
      if (response.data.access_token) {
        sessionStorage.setItem("access_token", response.data.access_token)
      }
      if (response.data.refresh_token) {
        sessionStorage.setItem("refresh_token", response.data.refresh_token)
      }

      // Fetch and store fresh user data after token refresh
      try {
        await this.fetchCurrentUser()
      } catch (userError) {
        console.warn("[AuthService] Failed to fetch user data after token refresh:", userError)
        // Continue even if user fetch fails - token refresh was successful
      }

      // Schedule next refresh
      this.scheduleTokenRefresh()

      // Dispatch event to notify components
      this.dispatchAuthRefreshEvent()

      return response.data
    } catch (error) {
      console.error("[AuthService] Failed to refresh token:", error)
      // Clear tokens on refresh failure
      this.clearTokens()
      throw error
    } finally {
      this.isRefreshing = false
    }
  }

  /**
   * Logout user and clear tokens
   * Also clears Auth0 session by redirecting to Auth0 logout URL
   */
  async logout(): Promise<LogoutResponse | null> {
    try {
      const accessToken = sessionStorage.getItem("access_token")
      const refreshToken = sessionStorage.getItem("refresh_token")

      if (accessToken && refreshToken) {
        const response = await axios.post<LogoutResponse>(
          `${this.baseUrl}${API_ENDPOINTS.auth.logout}`,
          { refresh_token: refreshToken },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          }
        )

        // Clear local tokens first
        this.clearTokens()

        return response.data
      }

      // No tokens to logout with
      this.clearTokens()
      return null
    } catch (error) {
      console.error("Logout request failed:", error)
      // Always clear tokens, even if logout request fails
      this.clearTokens()
      return null
    }
  }

  /**
   * Request a password reset email via Auth0
   * Endpoint: POST /v1/forgot-password/
   * Payload: { email }
   */
  async forgotPassword(email: string): Promise<void> {
    await axios.post(`${this.baseUrl}${API_ENDPOINTS.auth.forgotPassword}`, { email }, { timeout: 10000 })
  }

  /**
   * Get current access token
   */
  getAccessToken(): string | null {
    return sessionStorage.getItem("access_token")
  }

  /**
   * Check if current access token is expired
   * @param bufferSeconds - Consider expired if expiring within this buffer (default: 60s)
   * @returns true if expired or expiring soon
   */
  isAccessTokenExpired(bufferSeconds: number = 60): boolean {
    const token = this.getAccessToken()
    if (!token) {
      return true
    }
    return isTokenExpired(token, bufferSeconds)
  }

  /**
   * Check if access token should be refreshed (expired or expiring soon)
   * @param thresholdSeconds - Refresh threshold in seconds (default: 1 minute - industry standard)
   * @returns true if token should be refreshed
   */
  shouldRefreshAccessToken(thresholdSeconds: number = 60): boolean {
    const token = this.getAccessToken()
    if (!token) {
      return false // No token to refresh
    }
    return shouldRefreshToken(token, thresholdSeconds)
  }

  /**
   * Get time until access token expires
   * @returns Seconds until expiration, or 0 if expired/invalid
   */
  getTokenExpiryTime(): number {
    const token = this.getAccessToken()
    if (!token) {
      return 0
    }
    return getTimeUntilExpiry(token)
  }

  /**
   * Initialize authentication and refresh token if expired
   * This should be called on app initialization (page load/reload)
   * Uses a singleton pattern to prevent multiple simultaneous initializations
   * Once initialized, the result is cached for the entire session
   * @returns Promise that resolves to true if authenticated, false otherwise
   */
  async initializeAuth(): Promise<boolean> {
    // If already initialized or initializing, return the cached promise
    if (this.initializationPromise) {
      return this.initializationPromise
    }

    // Create and cache the initialization promise
    this.initializationPromise = this._performInitialization()

    return this.initializationPromise
  }

  /**
   * Internal method that performs the actual initialization logic
   * @private
   */
  private async _performInitialization(): Promise<boolean> {
    try {
      const accessToken = this.getAccessToken()
      const refreshToken = sessionStorage.getItem("refresh_token")

      // No tokens at all - user is not authenticated
      if (!accessToken && !refreshToken) {
        return false
      }

      // No refresh token but have access token - check if valid
      if (!refreshToken) {
        if (this.isAccessTokenExpired(0)) {
          this.clearTokens()
          return false
        }
        this.scheduleTokenRefresh()
        return true
      }

      // Proactively refresh if token is expired or expiring within 1 minute (industry standard)
      const shouldRefresh = this.shouldRefreshAccessToken(60) // 60 seconds threshold
      const expiryTime = this.getTokenExpiryTime()

      if (shouldRefresh) {
        try {
          await this.refreshToken()
          return true
        } catch (error) {
          console.error("[AuthService] Failed to refresh token on initialization:", error)
          this.clearTokens()
          return false
        }
      }

      // Token is valid and not expiring soon - schedule refresh for later
      console.log(`[AuthService] Access token is valid (expires in ${expiryTime}s)`)
      this.scheduleTokenRefresh()
      return true
    } catch (error) {
      console.error("[AuthService] Error initializing auth:", error)
      return false
    }
  }

  /**
   * Schedule automatic token refresh before expiration
   * Refreshes token when it has 1 minute remaining (industry standard)
   * @private
   */
  private scheduleTokenRefresh(): void {
    // Clear any existing timer
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }

    const expiryTime = this.getTokenExpiryTime()
    if (expiryTime <= 0) {
      return
    }

    // Schedule refresh 1 minute before expiration (or immediately if less than 1 min remaining)
    // Industry standard: refresh proactively to avoid service interruption
    const refreshThreshold = 60 // 60 seconds (1 minute)
    const timeUntilRefresh = Math.max(0, (expiryTime - refreshThreshold) * 1000) // Convert to milliseconds

    this.refreshTimer = setTimeout(async () => {
      try {
        await this.refreshToken()
      } catch (error) {
        console.error("[AuthService] Background token refresh failed:", error)
        // Token refresh failed - user will be logged out on next request
      }
    }, timeUntilRefresh)
  }

  /**
   * Cancel scheduled token refresh
   * @private
   */
  private cancelTokenRefresh(): void {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer)
      this.refreshTimer = null
    }
  }

  /**
   * Fetch current user data from backend
   * Endpoint: GET /auth/status/
   * Returns fresh user data from the backend
   */
  async fetchCurrentUser(): Promise<any> {
    const accessToken = sessionStorage.getItem("access_token")
    if (!accessToken) {
      throw new Error("No access token available")
    }

    try {
      const response = await axios.get(`${this.baseUrl}${API_ENDPOINTS.auth.status}`, {
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        timeout: 5000,
      })

      // Update user in sessionStorage with fresh data
      if (response.data.user) {
        sessionStorage.setItem("user", JSON.stringify(response.data.user))
        return response.data.user
      }

      // If user data is at root level
      if (response.data.email) {
        sessionStorage.setItem("user", JSON.stringify(response.data))
        return response.data
      }

      return response.data
    } catch (error) {
      console.error("Failed to fetch current user:", error)
      throw error
    }
  }

  /**
   * Fetch complete user profile with member details
   * Endpoint: GET /auth/user/
   * Returns complete user profile including member_profile and metadata
   */
  async fetchUserProfile(): Promise<any> {
    try {
      return await httpClientInstance.get(API_ENDPOINTS.auth.user)
    } catch (error) {
      console.error("Failed to fetch user profile:", error)
      throw error
    }
  }

  /**
   * Fetch account status
   * Endpoint: GET /account/me/ (v2)
   * Returns authenticated user details including account status, tier, AWS credentials, and payment info
   * Uses httpClient for automatic token refresh on 403 errors
   * Maps the nested response to a flattened AccountStatus interface
   */
  async fetchAccountStatus(): Promise<AccountStatus> {
    try {
      // Use httpClient for consistent error handling and automatic token refresh
      const response: AccountMeResponse = await httpClientInstance.get(API_ENDPOINTS.account.me)

      // Map the nested response to a flattened AccountStatus
      const accountStatus: AccountStatus = {
        // User-level fields
        auth0_user_id: response.user.auth0_user_id,
        email: response.user.email,
        name: response.user.name,
        picture: response.user.picture,
        last_login_at: response.user.last_login_at,
        last_ip_address: response.user.last_ip_address,
        roles: response.user.roles || [],
        user_type: response.user_type,
        // Metadata fields
        nickname: response.user.metadata?.nickname || "",
        email_verified: response.user.metadata?.email_verified || false,
        // Member profile fields
        monthly_budget: response.user.member_profile?.monthly_budget || null,
        billing_email: response.user.member_profile?.billing_email || null,
        contact_number: response.user.member_profile?.contact_number || null,
        member_is_active: response.user.member_profile?.is_active ?? null,
        // Owner profile fields
        owner_monthly_budget: response.user.owner_profile?.monthly_budget || null,
        contact_name: response.user.owner_profile?.contact_name || null,
        owner_is_active: response.user.owner_profile?.is_active ?? null,
        // Account-level fields - sourced from account or member data depending on user_type
        ...(response.user.account
          ? {
              id: response.user.account.id,
              username: response.user.account.username,
              first_name: response.user.account.first_name,
              middle_name: response.user.account.middle_name,
              last_name: response.user.account.last_name,
              phone: response.user.account.phone,
              cloudspry_tier: response.user.account.cloudspry_tier,
              account_type: response.user.account.account_type,
              company_name: response.user.account.company_name,
              website: response.user.account.website,
              has_aws_credentials: response.user.account.has_aws_credentials,
              aws_validation_status: response.user.account.aws_validation_status,
              aws_connection_method: response.user.account.aws_connection_method,
              aws_account_id: response.user.account.aws_account_id,
              aws_is_root_user: response.user.account.aws_is_root_user,
              has_credit_card: response.user.account.has_credit_card,
              credit_card_last4: response.user.account.credit_card_last4,
              credit_card_brand: response.user.account.credit_card_brand,
              personal_info: response.user.account.personal_info,
              business_info: response.user.account.business_info,
              created_at: response.user.account.created_at,
              updated_at: response.user.account.updated_at,
            }
          : {
              id: response.user.sub_account!.id,
              username: response.user.sub_account!.username,
              first_name: response.user.sub_account!.first_name,
              last_name: response.user.sub_account!.last_name,
              created_at: response.user.sub_account!.created_at,
              updated_at: response.user.sub_account!.updated_at,
              sub_account: response.user.sub_account,
            }),
      }

      return accountStatus
    } catch (error) {
      console.error("[AuthService] Failed to fetch account status:", error)
      throw error
    }
  }

  /**
   * Get current user from sessionStorage
   */
  getCurrentUser(): any | null {
    const userStr = sessionStorage.getItem("user")
    return userStr ? JSON.parse(userStr) : null
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return !!this.getAccessToken()
  }

  /**
   * Clear all auth tokens and user data
   */
  clearTokens(): void {
    // Cancel any scheduled token refresh
    this.cancelTokenRefresh()

    // Clear sessionStorage tokens
    sessionStorage.removeItem("access_token")
    sessionStorage.removeItem("refresh_token")
    sessionStorage.removeItem("user")
    sessionStorage.removeItem("auth_token") // Legacy token key
    sessionStorage.removeItem("oauth_state")
    sessionStorage.removeItem("oauth_code_challenge")

    // Reset initialization state to allow re-initialization after logout
    this.initializationPromise = null
  }

  /**
   * Dispatch custom event to notify components of auth state change
   * Used after token refresh or logout to trigger re-initialization
   */
  dispatchAuthRefreshEvent(): void {
    const event = new CustomEvent("auth-refreshed", {
      detail: {
        accessToken: this.getAccessToken(),
        refreshToken: sessionStorage.getItem("refresh_token"),
        user: this.getCurrentUser(),
        timestamp: Date.now(),
      },
    })
    window.dispatchEvent(event)
  }
}

// Export singleton instance
export const authService = new AuthService()
